import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../design-system/theme';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  HeaderActions,
  PrimaryButton,
  SecondaryButton,
  Table,
  Th,
  Td,
  StatusBadge,
  SearchInput,
  FilterGroup,
  FilterButton,
  EmptyState,
  LoadingSpinner,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  ModalFooter,
  ErrorMessage,
  SuccessMessage,
} from '../styles/adminShared';
import { adminApi, Region, RegionRequest, RegionValidationResponse } from '../services/adminApi';

// Extended styled components for Region page
const RegionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const RegionCard = styled.div<{ $active?: boolean }>`
  background: #ffffff;
  border: 1px solid ${props => props.$active ? theme.success : theme.border};
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const RegionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const RegionInfo = styled.div`
  flex: 1;
`;

const RegionCode = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.primary};
  background: ${theme.infoLight};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 0.5rem;
`;

const RegionName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 0.5rem 0;
`;

const RegionDescription = styled.p`
  font-size: 0.875rem;
  color: ${theme.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const RegionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.border};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${theme.textSecondary};

  svg {
    width: 14px;
    height: 14px;
    color: ${theme.textSubtle};
  }
`;

const FrameworkBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: ${theme.dangerLight};
  color: ${theme.accent};
`;

const RegionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'success' }>`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'danger': return theme.danger;
      case 'success': return theme.success;
      default: return theme.primary;
    }
  }};
  background: transparent;
  color: ${props => {
    switch (props.$variant) {
      case 'danger': return theme.danger;
      case 'success': return theme.success;
      default: return theme.primary;
    }
  }};
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      switch (props.$variant) {
        case 'danger': return theme.danger;
        case 'success': return theme.success;
        default: return theme.primary;
      }
    }};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ValidationSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${theme.surfaceMuted};
  border-radius: 8px;
`;

const ValidationTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 0.75rem 0;
`;

const ValidationItem = styled.div<{ $type: 'error' | 'warning' | 'recommendation' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.8125rem;
  padding: 0.5rem;
  background: ${props => {
    switch (props.$type) {
      case 'error': return theme.dangerLight;
      case 'warning': return theme.warningLight;
      default: return theme.infoLight;
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'error': return theme.danger;
      case 'warning': return theme.warning;
      default: return theme.info;
    }
  }};
  border-radius: 4px;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${theme.textPrimary};
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border: 1px solid ${theme.border};
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.primary};
    background: ${theme.infoLight};
  }

  input[type="checkbox"] {
    margin: 0;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionDivider = styled.div`
  margin: 1.5rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.border};
`;

const SectionLabel = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 1rem 0;
`;

// Compliance frameworks
const COMPLIANCE_FRAMEWORKS = [
  { value: 'GDPR', label: 'GDPR (EU)' },
  { value: 'UK_GDPR', label: 'UK GDPR' },
  { value: 'CCPA', label: 'CCPA (California)' },
  { value: 'DPDP', label: 'DPDP (India)' },
  { value: 'PDPA', label: 'PDPA (Singapore)' },
  { value: 'LGPD', label: 'LGPD (Brazil)' },
  { value: 'HIPAA', label: 'HIPAA (US Healthcare)' },
  { value: 'PIPEDA', label: 'PIPEDA (Canada)' },
  { value: 'APPI', label: 'APPI (Japan)' },
  { value: 'PIPA', label: 'PIPA (South Korea)' },
  { value: 'POPIA', label: 'POPIA (South Africa)' },
  { value: 'UAE_PDPL', label: 'UAE PDPL' },
  { value: 'SAUDI_PDPL', label: 'Saudi PDPL' },
  { value: 'PRIVACY_ACT', label: 'Privacy Act (Australia)' },
];

const AWS_REGIONS = [
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
];

const DATA_RESIDENCY_LEVELS = [
  { value: 'STRICT', label: 'Strict - Data must stay in region' },
  { value: 'PREFERRED', label: 'Preferred - Data preferably stays in region' },
  { value: 'FLEXIBLE', label: 'Flexible - Data can be moved with consent' },
];

const Regions: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [validation, setValidation] = useState<RegionValidationResponse | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<RegionRequest>({
    regionCode: '',
    displayName: '',
    description: '',
    awsRegion: '',
    primaryComplianceFramework: '',
    dataResidencyLevel: 'PREFERRED',
    minimumConsentAge: 18,
    requiresExplicitOptIn: true,
    allowsCrossBorderTransfer: true,
    primaryLanguage: 'en',
    timezone: 'UTC',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadRegions();
  }, []);

  const loadRegions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getAllRegions();
      setRegions(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load regions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRegion = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await adminApi.createRegion(formData);
      setSuccess('Region created successfully');
      setShowCreateModal(false);
      resetForm();
      loadRegions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create region');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRegion = async () => {
    if (!selectedRegion || !validateForm()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await adminApi.updateRegion(selectedRegion.regionCode, formData);
      setSuccess('Region updated successfully');
      setShowEditModal(false);
      resetForm();
      loadRegions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update region');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivateRegion = async (region: Region) => {
    try {
      setError(null);
      await adminApi.activateRegion(region.regionCode);
      setSuccess(`Region ${region.displayName} activated`);
      loadRegions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate region');
    }
  };

  const handleDeactivateRegion = async (region: Region) => {
    try {
      setError(null);
      await adminApi.deactivateRegion(region.regionCode);
      setSuccess(`Region ${region.displayName} deactivated`);
      loadRegions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate region');
    }
  };

  const handleValidateRegion = async (region: Region) => {
    try {
      setError(null);
      const result = await adminApi.validateRegion(region.regionCode);
      setValidation(result);
      setSelectedRegion(region);
      setShowValidationModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate region');
    }
  };



  const openEditModal = (region: Region) => {
    setSelectedRegion(region);
    setFormData({
      regionCode: region.regionCode,
      displayName: region.displayName,
      description: region.description || '',
      awsRegion: region.awsRegion,
      primaryComplianceFramework: region.primaryComplianceFramework || '',
      dataResidencyLevel: region.dataResidencyLevel || 'PREFERRED',
      minimumConsentAge: region.minimumConsentAge || 18,
      requiresExplicitOptIn: region.requiresExplicitOptIn ?? true,
      allowsCrossBorderTransfer: region.allowsCrossBorderTransfer ?? true,
      primaryLanguage: region.primaryLanguage || 'en',
      timezone: region.timezone || 'UTC',
      supervisoryAuthority: region.supervisoryAuthority,
      authorityWebsite: region.authorityWebsite,
      requiresDpo: region.requiresDpo,
      regionalDpoEmail: region.regionalDpoEmail,
    });
    setShowEditModal(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.regionCode?.trim()) {
      errors.regionCode = 'Region code is required';
    } else if (!/^[A-Z]{2}(-[A-Z]{2,3})?$/.test(formData.regionCode)) {
      errors.regionCode = 'Region code must be ISO format (e.g., IN, US, US-CA)';
    }
    
    if (!formData.displayName?.trim()) {
      errors.displayName = 'Display name is required';
    }
    
    if (!formData.awsRegion?.trim()) {
      errors.awsRegion = 'AWS region is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      regionCode: '',
      displayName: '',
      description: '',
      awsRegion: '',
      primaryComplianceFramework: '',
      dataResidencyLevel: 'PREFERRED',
      minimumConsentAge: 18,
      requiresExplicitOptIn: true,
      allowsCrossBorderTransfer: true,
      primaryLanguage: 'en',
      timezone: 'UTC',
    });
    setFormErrors({});
    setSelectedRegion(null);
  };

  // Filter regions
  const filteredRegions = regions.filter(region => {
    const matchesSearch = region.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.regionCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.primaryComplianceFramework?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && region.active) ||
      (filterStatus === 'inactive' && !region.active);
    
    return matchesSearch && matchesStatus;
  });

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading regions...</LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <PageTitle>Region Management</PageTitle>
          <PageDescription>
            Configure geographic regions for data residency and compliance requirements
          </PageDescription>
        </div>
        <HeaderActions>
          <SecondaryButton onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}>
            {viewMode === 'grid' ? '📋 Table View' : '📦 Grid View'}
          </SecondaryButton>
          <PrimaryButton onClick={() => setShowCreateModal(true)}>
            + Add Region
          </PrimaryButton>
        </HeaderActions>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <FilterGroup>
        <SearchInput
          type="text"
          placeholder="Search regions..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSearchTerm(e.target.value)}
        />
        <FilterButton $active={filterStatus === 'all'} onClick={() => setFilterStatus('all')}>
          All
        </FilterButton>
        <FilterButton $active={filterStatus === 'active'} onClick={() => setFilterStatus('active')}>
          Active
        </FilterButton>
        <FilterButton $active={filterStatus === 'inactive'} onClick={() => setFilterStatus('inactive')}>
          Inactive
        </FilterButton>
      </FilterGroup>

      {filteredRegions.length === 0 ? (
        <EmptyState>
          <h3>No regions found</h3>
          <p>
            {searchTerm || filterStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first region to get started with multi-region consent management'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <PrimaryButton onClick={() => setShowCreateModal(true)}>
              + Add Region
            </PrimaryButton>
          )}
        </EmptyState>
      ) : viewMode === 'grid' ? (
        <RegionGrid>
          {filteredRegions.map(region => (
            <RegionCard key={region.regionCode} $active={region.active}>
              <RegionHeader>
                <RegionInfo>
                  <RegionCode>{region.regionCode}</RegionCode>
                  <RegionName>{region.displayName}</RegionName>
                  {region.description && (
                    <RegionDescription>{region.description}</RegionDescription>
                  )}
                </RegionInfo>
                <StatusBadge $status={region.active ? 'ACTIVE' : 'INACTIVE'}>
                  {region.active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </RegionHeader>

              <RegionMeta>
                <MetaItem>
                  🌐 {AWS_REGIONS.find(r => r.value === region.awsRegion)?.label || region.awsRegion}
                </MetaItem>
                {region.primaryComplianceFramework && (
                  <FrameworkBadge>{region.primaryComplianceFramework}</FrameworkBadge>
                )}
                {region.minimumConsentAge && (
                  <MetaItem>👤 Age {region.minimumConsentAge}+</MetaItem>
                )}
                {region.infrastructureReady && (
                  <MetaItem>✓ Infrastructure Ready</MetaItem>
                )}
              </RegionMeta>

              <RegionActions>
                <ActionButton onClick={() => openEditModal(region)}>Edit</ActionButton>
                <ActionButton onClick={() => handleValidateRegion(region)}>Validate</ActionButton>
                {region.active ? (
                  <ActionButton $variant="danger" onClick={() => handleDeactivateRegion(region)}>
                    Deactivate
                  </ActionButton>
                ) : (
                  <ActionButton $variant="success" onClick={() => handleActivateRegion(region)}>
                    Activate
                  </ActionButton>
                )}
              </RegionActions>
            </RegionCard>
          ))}
        </RegionGrid>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Region</Th>
              <Th>AWS Region</Th>
              <Th>Framework</Th>
              <Th>Data Residency</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredRegions.map(region => (
              <tr key={region.regionCode}>
                <Td>
                  <strong>{region.displayName}</strong>
                  <br />
                  <small style={{ color: theme.textSecondary }}>{region.regionCode}</small>
                </Td>
                <Td>{AWS_REGIONS.find(r => r.value === region.awsRegion)?.label || region.awsRegion}</Td>
                <Td>{region.primaryComplianceFramework || '-'}</Td>
                <Td>{region.dataResidencyLevel || '-'}</Td>
                <Td>
                  <StatusBadge $status={region.active ? 'ACTIVE' : 'INACTIVE'}>
                    {region.active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </Td>
                <Td>
                  <ActionButton onClick={() => openEditModal(region)}>Edit</ActionButton>
                  {' '}
                  <ActionButton onClick={() => handleValidateRegion(region)}>Validate</ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <ModalOverlay $show={true} onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <ModalHeader>
              <ModalTitle>{showCreateModal ? 'Create Region' : 'Edit Region'}</ModalTitle>
              <CloseButton onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormRow>
                <FormGroup>
                  <Label>Region Code *</Label>
                  <Input
                    value={formData.regionCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, regionCode: e.target.value.toUpperCase() })}
                    placeholder="e.g., IN, US, EU, US-CA"
                    disabled={showEditModal}
                    $hasError={!!formErrors.regionCode}
                  />
                  {formErrors.regionCode && <ErrorMessage>{formErrors.regionCode}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>Display Name *</Label>
                  <Input
                    value={formData.displayName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="e.g., India, United States"
                    $hasError={!!formErrors.displayName}
                  />
                  {formErrors.displayName && <ErrorMessage>{formErrors.displayName}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description of this region's configuration"
                  rows={2}
                />
              </FormGroup>

              <SectionDivider>
                <SectionLabel>🌐 Infrastructure</SectionLabel>
              </SectionDivider>

              <FormRow>
                <FormGroup>
                  <Label>AWS Region *</Label>
                  <Select
                    value={formData.awsRegion}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, awsRegion: e.target.value })}
                    $hasError={!!formErrors.awsRegion}
                  >
                    <option value="">Select AWS Region</option>
                    {AWS_REGIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Select>
                  {formErrors.awsRegion && <ErrorMessage>{formErrors.awsRegion}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>Data Residency Level</Label>
                  <Select
                    value={formData.dataResidencyLevel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, dataResidencyLevel: e.target.value })}
                  >
                    {DATA_RESIDENCY_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </Select>
                </FormGroup>
              </FormRow>

              <SectionDivider>
                <SectionLabel>⚖️ Compliance</SectionLabel>
              </SectionDivider>

              <FormRow>
                <FormGroup>
                  <Label>Primary Compliance Framework</Label>
                  <Select
                    value={formData.primaryComplianceFramework}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, primaryComplianceFramework: e.target.value })}
                  >
                    <option value="">Select Framework</option>
                    {COMPLIANCE_FRAMEWORKS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Minimum Consent Age</Label>
                  <Input
                    type="number"
                    value={formData.minimumConsentAge}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, minimumConsentAge: parseInt(e.target.value) || 18 })}
                    min={13}
                    max={21}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Consent Requirements</Label>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.requiresExplicitOptIn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, requiresExplicitOptIn: e.target.checked })}
                    />
                    Requires Explicit Opt-In
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.requiresDoubleOptIn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, requiresDoubleOptIn: e.target.checked })}
                    />
                    Requires Double Opt-In
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.allowsCrossBorderTransfer}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, allowsCrossBorderTransfer: e.target.checked })}
                    />
                    Allow Cross-Border Transfer
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.easyWithdrawalRequired}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, easyWithdrawalRequired: e.target.checked })}
                    />
                    Easy Withdrawal Required
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>

              <SectionDivider>
                <SectionLabel>🏛️ Supervisory Authority</SectionLabel>
              </SectionDivider>

              <FormRow>
                <FormGroup>
                  <Label>Supervisory Authority Name</Label>
                  <Input
                    value={formData.supervisoryAuthority || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, supervisoryAuthority: e.target.value })}
                    placeholder="e.g., ICO, CNIL, DPIIT"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Authority Website</Label>
                  <Input
                    value={formData.authorityWebsite || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, authorityWebsite: e.target.value })}
                    placeholder="https://..."
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Requires DPO</Label>
                  <Select
                    value={formData.requiresDpo ? 'yes' : 'no'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, requiresDpo: e.target.value === 'yes' })}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Select>
                </FormGroup>
                {formData.requiresDpo && (
                  <FormGroup>
                    <Label>Regional DPO Email</Label>
                    <Input
                      type="email"
                      value={formData.regionalDpoEmail || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, regionalDpoEmail: e.target.value })}
                      placeholder="dpo@example.com"
                    />
                  </FormGroup>
                )}
              </FormRow>

              <SectionDivider>
                <SectionLabel>🌍 Localization</SectionLabel>
              </SectionDivider>

              <FormRow>
                <FormGroup>
                  <Label>Primary Language</Label>
                  <Select
                    value={formData.primaryLanguage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, primaryLanguage: e.target.value })}
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="de">German</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="zh">Chinese</option>
                    <option value="ar">Arabic</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Timezone</Label>
                  <Input
                    value={formData.timezone || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, timezone: e.target.value })}
                    placeholder="e.g., Asia/Kolkata, Europe/Berlin"
                  />
                </FormGroup>
              </FormRow>
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={showCreateModal ? handleCreateRegion : handleUpdateRegion}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : showCreateModal ? 'Create Region' : 'Save Changes'}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Validation Modal */}
      {showValidationModal && validation && selectedRegion && (
        <ModalOverlay $show={true} onClick={() => { setShowValidationModal(false); setValidation(null); }}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Validation: {selectedRegion.displayName}</ModalTitle>
              <CloseButton onClick={() => { setShowValidationModal(false); setValidation(null); }}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <StatusBadge $status={validation.valid ? 'ACTIVE' : 'FAILED'} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  {validation.valid ? '✓ Ready for Activation' : '✗ Not Ready'}
                </StatusBadge>
              </div>

              <ValidationSection>
                <ValidationTitle>Infrastructure Status</ValidationTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  <MetaItem>{validation.dynamoDbReady ? '✓' : '✗'} DynamoDB</MetaItem>
                  <MetaItem>{validation.s3Ready ? '✓' : '✗'} S3</MetaItem>
                  <MetaItem>{validation.kmsReady ? '✓' : '✗'} KMS</MetaItem>
                </div>
              </ValidationSection>

              {validation.errors.length > 0 && (
                <ValidationSection>
                  <ValidationTitle>❌ Errors ({validation.errors.length})</ValidationTitle>
                  {validation.errors.map((err, idx) => (
                    <ValidationItem key={idx} $type="error">{err}</ValidationItem>
                  ))}
                </ValidationSection>
              )}

              {validation.warnings.length > 0 && (
                <ValidationSection>
                  <ValidationTitle>⚠️ Warnings ({validation.warnings.length})</ValidationTitle>
                  {validation.warnings.map((warn, idx) => (
                    <ValidationItem key={idx} $type="warning">{warn}</ValidationItem>
                  ))}
                </ValidationSection>
              )}

              {validation.recommendations.length > 0 && (
                <ValidationSection>
                  <ValidationTitle>💡 Recommendations ({validation.recommendations.length})</ValidationTitle>
                  {validation.recommendations.map((rec, idx) => (
                    <ValidationItem key={idx} $type="recommendation">{rec}</ValidationItem>
                  ))}
                </ValidationSection>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => { setShowValidationModal(false); setValidation(null); }}>
                Close
              </SecondaryButton>
              {validation.valid && !selectedRegion.active && (
                <PrimaryButton onClick={() => {
                  handleActivateRegion(selectedRegion);
                  setShowValidationModal(false);
                  setValidation(null);
                }}>
                  Activate Region
                </PrimaryButton>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Regions;
